import { GraphQlBuilder } from "@contember/client";
import { BindingError } from "../BindingError.js";
class TreeParameterMerger {
  static mergeHasOneRelationsWithSamePlaceholders(original, fresh) {
    return {
      field: original.field,
      filter: original.filter,
      reducedBy: original.reducedBy,
      expectedMutation: this.mergeExpectedRelationMutation(original.expectedMutation, fresh.expectedMutation),
      setOnCreate: this.mergeSetOnCreate(original.setOnCreate, fresh.setOnCreate),
      isNonbearing: original.isNonbearing && fresh.isNonbearing,
      eventListeners: this.mergeSingleEntityEventListeners(original.eventListeners, fresh.eventListeners)
    };
  }
  static mergeHasManyRelationsWithSamePlaceholders(original, fresh) {
    if (original.initialEntityCount !== fresh.initialEntityCount) {
      throw new BindingError(
        `Detected hasMany relations on the same field '${original.field}' with different preferred initial entity counts: '${original.initialEntityCount}' and '${fresh.initialEntityCount}' respectively.`
      );
    }
    return {
      field: original.field,
      filter: original.filter,
      orderBy: original.orderBy,
      offset: original.offset,
      limit: original.limit,
      expectedMutation: this.mergeExpectedRelationMutation(original.expectedMutation, fresh.expectedMutation),
      setOnCreate: this.mergeSetOnCreate(original.setOnCreate, fresh.setOnCreate),
      isNonbearing: original.isNonbearing && fresh.isNonbearing,
      initialEntityCount: original.initialEntityCount,
      childEventListeners: this.mergeSingleEntityEventListeners(
        original.childEventListeners,
        fresh.childEventListeners
      ),
      eventListeners: this.mergeEntityListEventListeners(original.eventListeners, fresh.eventListeners)
    };
  }
  static mergeEntitySubTreeParametersWithSamePlaceholders(original, fresh) {
    if (original.isCreating) {
      return {
        isCreating: original.isCreating,
        entityName: original.entityName,
        alias: this.mergeSubTreeAliases(original.alias, fresh.alias),
        setOnCreate: this.mergeSetOnCreate(original.setOnCreate, fresh.setOnCreate),
        expectedMutation: this.mergeExpectedQualifiedEntityMutation(original.expectedMutation, fresh.expectedMutation),
        isNonbearing: original.isNonbearing && fresh.isNonbearing,
        hasOneRelationPath: original.hasOneRelationPath,
        eventListeners: this.mergeSingleEntityEventListeners(original.eventListeners, fresh.eventListeners)
      };
    }
    return {
      isCreating: original.isCreating,
      where: original.where,
      entityName: original.entityName,
      filter: original.filter,
      alias: this.mergeSubTreeAliases(original.alias, fresh.alias),
      setOnCreate: this.mergeSetOnCreate(original.setOnCreate, fresh.setOnCreate),
      expectedMutation: this.mergeExpectedQualifiedEntityMutation(original.expectedMutation, fresh.expectedMutation),
      isNonbearing: original.isNonbearing && fresh.isNonbearing,
      hasOneRelationPath: original.hasOneRelationPath,
      eventListeners: this.mergeSingleEntityEventListeners(original.eventListeners, fresh.eventListeners)
    };
  }
  static mergeEntityListSubTreeParametersWithSamePlaceholders(original, fresh) {
    if (original.initialEntityCount !== fresh.initialEntityCount) {
      throw new BindingError(
        `Detected sub trees of the same entity '${original.entityName}' with different preferred initial entity counts: '${original.initialEntityCount}' and '${fresh.initialEntityCount}' respectively.`
      );
    }
    if (original.isCreating) {
      return {
        isCreating: original.isCreating,
        entityName: original.entityName,
        alias: this.mergeSubTreeAliases(original.alias, fresh.alias),
        setOnCreate: this.mergeSetOnCreate(original.setOnCreate, fresh.setOnCreate),
        expectedMutation: this.mergeExpectedQualifiedEntityMutation(original.expectedMutation, fresh.expectedMutation),
        isNonbearing: original.isNonbearing && fresh.isNonbearing,
        hasOneRelationPath: original.hasOneRelationPath,
        childEventListeners: this.mergeSingleEntityEventListeners(
          original.childEventListeners,
          fresh.childEventListeners
        ),
        eventListeners: this.mergeEntityListEventListeners(original.eventListeners, fresh.eventListeners),
        initialEntityCount: original.initialEntityCount
      };
    }
    return {
      isCreating: original.isCreating,
      entityName: original.entityName,
      filter: original.filter,
      orderBy: original.orderBy,
      offset: original.offset,
      limit: original.limit,
      alias: this.mergeSubTreeAliases(original.alias, fresh.alias),
      setOnCreate: this.mergeSetOnCreate(original.setOnCreate, fresh.setOnCreate),
      expectedMutation: this.mergeExpectedQualifiedEntityMutation(original.expectedMutation, fresh.expectedMutation),
      isNonbearing: original.isNonbearing && fresh.isNonbearing,
      hasOneRelationPath: original.hasOneRelationPath,
      childEventListeners: this.mergeSingleEntityEventListeners(
        original.childEventListeners,
        fresh.childEventListeners
      ),
      eventListeners: this.mergeEntityListEventListeners(original.eventListeners, fresh.eventListeners),
      initialEntityCount: original.initialEntityCount
    };
  }
  static mergeEntityCreationParameters(original, fresh) {
    return {
      isNonbearing: original.isNonbearing && fresh.isNonbearing,
      setOnCreate: this.mergeSetOnCreate(original.setOnCreate, fresh.setOnCreate)
    };
  }
  static mergeSetOnCreate(original, fresh) {
    if (original === void 0 && fresh === void 0) {
      return void 0;
    }
    if (original === void 0) {
      return fresh;
    }
    if (fresh === void 0) {
      return original;
    }
    const originalCopy = { ...original };
    for (const field in fresh) {
      if (field in originalCopy) {
        const fromOriginal = originalCopy[field];
        const fromFresh = fresh[field];
        if (fromOriginal instanceof GraphQlBuilder.GraphQlLiteral) {
          if (fromFresh instanceof GraphQlBuilder.GraphQlLiteral) {
            if (fromOriginal.value === fromFresh.value) {
              continue;
            } else {
              throw new BindingError();
            }
          } else {
            throw new BindingError();
          }
        }
        if (typeof fromOriginal === "object") {
          if (fromFresh instanceof GraphQlBuilder.GraphQlLiteral) {
            throw new BindingError();
          } else if (typeof fromFresh === "object") {
            const merged = this.mergeSetOnCreate(fromOriginal, fromFresh);
            if (merged !== void 0) {
              originalCopy[field] = merged;
            }
            continue;
          } else {
            throw new BindingError();
          }
        }
        if (typeof fromFresh === "string" || typeof fromFresh === "number") {
          if (fromOriginal === fromFresh) {
            continue;
          } else {
            throw new BindingError();
          }
        }
        throw new BindingError();
      } else {
        originalCopy[field] = fresh[field];
      }
    }
    return originalCopy;
  }
  static mergeSets(original, fresh) {
    const combinedSet = new Set(original);
    for (const item of fresh) {
      combinedSet.add(item);
    }
    return combinedSet;
  }
  static mergeParentEntityParameters(original, fresh) {
    if (original === fresh) {
      return original;
    }
    if (original === void 0) {
      return fresh;
    }
    if (fresh === void 0) {
      return original;
    }
    return {
      eventListeners: TreeParameterMerger.mergeSingleEntityEventListeners(
        original.eventListeners,
        fresh.eventListeners
      )
    };
  }
  static mergeInParentEntity(original, key, parentEntity) {
    if (!parentEntity) {
      return original;
    }
    return {
      ...original,
      [key]: TreeParameterMerger.mergeSingleEntityEventListeners(original[key], parentEntity.eventListeners)
    };
  }
  static mergeSingleEntityEventListeners(original, fresh) {
    if (original === void 0) {
      if (fresh === void 0) {
        return void 0;
      }
      return this.cloneSingleEntityEventListeners(fresh);
    }
    if (fresh === void 0) {
      return this.cloneSingleEntityEventListeners(original);
    }
    const store = this.cloneSingleEntityEventListeners(original);
    store.append(fresh);
    return store;
  }
  static mergeEntityListEventListeners(original, fresh) {
    if (original === void 0) {
      if (fresh === void 0) {
        return void 0;
      }
      return this.cloneEntityListEventListeners(fresh);
    }
    if (fresh === void 0) {
      return this.cloneEntityListEventListeners(original);
    }
    const store = this.cloneEntityListEventListeners(original);
    store.append(fresh);
    return store;
  }
  static cloneSingleEntityEventListeners(store) {
    return store.clone();
  }
  static cloneEntityListEventListeners(store) {
    return store.cloneDeep();
  }
  static mergeExpectedRelationMutation(original, fresh) {
    if (original === fresh) {
      return original;
    }
    if (original === "none") {
      return fresh;
    }
    if (fresh === "none") {
      return original;
    }
    return "anyMutation";
  }
  static mergeExpectedQualifiedEntityMutation(original, fresh) {
    if (original === fresh) {
      return original;
    }
    if (original === "none") {
      return fresh;
    }
    if (fresh === "none") {
      return original;
    }
    return "anyMutation";
  }
  static mergeSubTreeAliases(original, fresh) {
    if (original === void 0) {
      return fresh;
    }
    if (fresh === void 0) {
      return original;
    }
    return this.mergeSets(original, fresh);
  }
}
export {
  TreeParameterMerger
};
//# sourceMappingURL=TreeParameterMerger.js.map
